import React, { useContext, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Rendered under the title, inside the header (e.g. a cost readout) */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Max width of the modal container (default 500px) */
  maxWidth?: string;
  /** Whether user-initiated close, backdrop, and Escape dismissal is allowed. */
  dismissible?: boolean;
  /** Prevent every user dismissal path while an operation must stay visible. */
  isBusy?: boolean;
  /**
   * On phones, fill the whole screen (like the chat surface) instead of
   * floating as a centered card. Use for content-heavy modals.
   */
  fullScreenOnMobile?: boolean;
  /**
   * Hand scrolling to the content instead of scrolling the whole modal. The
   * header stays pinned and `children` becomes a flex column that fills the
   * remaining height, so a child can own an `overflow-y: auto` region and keep
   * a footer visible below it. Opt-in: without it the modal scrolls as a whole,
   * which is right for short dialogs.
   */
  scrollBody?: boolean;
  /**
   * Always occupy the full available height (90vh) instead of sizing to
   * content — for modals whose child is a height-filling layout (e.g. an
   * embedded chat). Pair with scrollBody.
   */
  fullHeight?: boolean;
  /**
   * ARIA role of the dialog container. Default `dialog`. Use `alertdialog`
   * for a confirmation that interrupts the user to warn of a consequence
   * they must acknowledge before proceeding (e.g. an irreversible lockout
   * risk), per the WAI-ARIA distinction between the two roles.
   */
  role?: 'dialog' | 'alertdialog';
}

/**
 * How deeply a modal is nested in the React tree. A modal opened from inside
 * another modal reads its parent's depth and adds one, which is what decides
 * the Escape order now that every modal is portalled to the body and no modal
 * DOM-contains another.
 */
const ModalDepthContext = React.createContext(0);

/**
 * Registry of open modals. Escape must dismiss only the topmost modal —
 * every open modal listens on document, so without this one keypress
 * would close a whole nested stack (e.g. the card detail behind a
 * confirm dialog). "Topmost" is the greatest nesting depth, falling back
 * to registration order between modals at the same depth. Depth is read
 * from React context rather than the DOM because modals are portalled:
 * mount order alone would be wrong, since a nested modal that opens in the
 * same commit as its parent registers first (effects run child-first).
 */
interface ModalStackEntry {
  ref: React.RefObject<HTMLDivElement>;
  depth: number;
}

const openModalStack: ModalStackEntry[] = [];
const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelector),
  ).filter(
    (element) =>
      !element.hasAttribute('hidden') &&
      element.getAttribute('aria-hidden') !== 'true',
  );
}

function focusModal(entry: ModalStackEntry): void {
  const container = entry.ref.current;
  if (!container) return;
  container.focus();
}

function isTopmostModal(entry: ModalStackEntry): boolean {
  const deepest = Math.max(...openModalStack.map((open) => open.depth));
  const uncovered = openModalStack.filter((open) => open.depth === deepest);
  return uncovered[uncovered.length - 1] === entry;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = '500px',
  dismissible = true,
  isBusy = false,
  fullScreenOnMobile = false,
  scrollBody = false,
  fullHeight = false,
  role = 'dialog',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const depth = useContext(ModalDepthContext) + 1;
  const stackEntryRef = useRef<ModalStackEntry>({ ref: modalRef, depth });

  // The page behind an open modal must never scroll (iOS included)
  useBodyScrollLock(isOpen);

  // Register in the open-modal stack while open
  useEffect(() => {
    if (!isOpen) return;
    const entry = stackEntryRef.current;
    entry.depth = depth;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    openModalStack.push(entry);
    if (isTopmostModal(entry)) focusModal(entry);
    return () => {
      const index = openModalStack.indexOf(entry);
      if (index !== -1) openModalStack.splice(index, 1);
      const remainingTopmost = openModalStack.find(isTopmostModal);
      if (remainingTopmost) {
        const remainingContainer = remainingTopmost.ref.current;
        if (
          previouslyFocused?.isConnected &&
          remainingContainer?.contains(previouslyFocused)
        ) {
          previouslyFocused.focus();
        } else {
          focusModal(remainingTopmost);
        }
      } else if (previouslyFocused?.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [depth, isOpen]);

  const canDismiss = dismissible && !isBusy;

  const handleBackdropMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (
      event.target === event.currentTarget &&
      canDismiss &&
      isTopmostModal(stackEntryRef.current)
    ) {
      onClose();
    }
  };

  // Close modal when pressing Escape key — topmost open modal only
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const entry = stackEntryRef.current;
      if (!isTopmostModal(entry)) return;
      if (canDismiss && event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !entry.ref.current) return;

      const focusable = focusableElements(entry.ref.current);
      if (focusable.length === 0) {
        event.preventDefault();
        entry.ref.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const activeIsContainer = active === entry.ref.current;
      if (
        event.shiftKey &&
        (activeIsContainer ||
          active === first ||
          !entry.ref.current.contains(active))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (activeIsContainer ||
          active === last ||
          !entry.ref.current.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canDismiss, isOpen, onClose]);

  if (!isOpen) return null;

  /**
   * Portalled to the body rather than rendered where it is declared. A modal
   * opened from inside another modal would otherwise be a descendant of that
   * modal's container, whose `backdrop-filter` makes it the containing block
   * for `position: fixed` — so the nested overlay was sized to the modal
   * behind it instead of the viewport, and the container's `overflow: hidden`
   * clipped everything outside it, including the nested modal's own footer.
   */
  return createPortal(
    <div className="delete-modal-overlay" onMouseDown={handleBackdropMouseDown}>
      <div
        ref={modalRef}
        className={`delete-modal-container${
          fullScreenOnMobile ? ' modal-fullscreen-mobile' : ''
        }`}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        aria-busy={isBusy || undefined}
        style={{
          maxWidth,
          maxHeight: '90vh',
          ...(fullHeight ? { height: '90vh' } : {}),
          // With scrollBody the container must not scroll — the child does, so
          // the header stays pinned and the child's own footer stays visible.
          overflow: scrollBody ? 'hidden' : 'auto',
          ...(scrollBody
            ? { display: 'flex', flexDirection: 'column', minHeight: 0 }
            : {}),
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            // Vertically centered against the title block so the close button
            // lines up with the middle of the title text, including when the
            // title wraps to multiple lines.
            alignItems: 'center',
            paddingBottom: '0.25rem',
            marginBottom: '0.5rem',
            borderBottom: '1px solid var(--border-color)',
            // Pinned above the scrolling child
            flex: '0 0 auto',
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              id={titleId}
              style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: 0,
                color: 'var(--text-color)',
              }}>
              {title}
            </h2>
            {subtitle && <div style={{ marginTop: '4px' }}>{subtitle}</div>}
          </div>
          <button
            type="button"
            aria-label="Close"
            disabled={!canDismiss}
            onClick={() => canDismiss && onClose()}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--secondary-text-color)',
              cursor: canDismiss ? 'pointer' : 'not-allowed',
              // Symmetric hit area keeps keyboard focus centered on the icon.
              padding: '0.25rem',
              marginLeft: '0.5rem',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // The app-wide `form button` rule expands buttons to the full
              // form width. Modals can be nested inside forms, so explicitly
              // keep this header action compact.
              width: 'auto',
              marginTop: 0,
              flex: '0 0 auto',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--secondary-text-color)';
            }}>
            <FaTimes />
          </button>
        </div>
        <ModalDepthContext.Provider value={depth}>
          {scrollBody ? (
            <div
              style={{
                flex: '1 1 auto',
                // Without this a flex child refuses to shrink below its content
                // height, and the child's overflow-y never engages.
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}>
              {children}
            </div>
          ) : (
            children
          )}
        </ModalDepthContext.Provider>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
